import { useToken } from "@gluestack-style/react";
import ItemListItem from "components/item/ItemListItem";
import ItemListItemReplacementSection from "components/item/ItemListItemReplacementSection";
import { Box, FlatList, HStack, Icon, VStack } from "components/ui";
import { H4, ParagraphSmall, Typography } from "components/ui/Typography";
import CheckBox from "expo-checkbox";
import { AlertCircle, TriangleAlertIcon } from "lucide-react-native";
import React from "react";
import { canBeFulfilled } from "screens/CartCheckoutLayout";
import { useIsMobile } from "utils/hooks/useIsMobile";

// Item Status Handler - Helper functions for status categorization
const itemStatusHandler = {
	getMainStatus: (status: number): number => {
		if (status >= 70) return 70;
		if (status >= 60) return 60;
		if (status >= 50) return 50;
		if (status >= 40) return 40;
		return 0;
	},

	getItemsByMainStatus: (cartLines: any[], cart: any, mainStatus: number) => {
		return cartLines.filter((item: any) => {
			const status = canBeFulfilled(item, cart);
			return itemStatusHandler.getMainStatus(status) === mainStatus;
		});
	},

	getStatusMessage: (status: number, item?: any, cart?: any): string => {
		const availability = item?.item.itemAvailability?.find((ia: any) => ia.locationId === cart?.locationId);
		const localAvailableQty = availability?.quantity ?? 0;
		const classification = availability?.classification ?? "";

		// For discontinued items in status 50, show total available across all locations
		const totalAvailableQty =
			classification === "Discontinued" && status === 50
				? item?.item.itemAvailability?.reduce((sum: number, location: any) => {
						return sum + (location.status !== "OutOfStock" ? location.quantity || 0 : 0);
					}, 0) || 0
				: localAvailableQty;

		switch (status) {
			case 70:
				const minimumQty = availability?.minimumQuantity ?? 0;
				return `🚫 Minimum quantity required: ${minimumQty} | Requested: ${item.quantity} | Please increase quantity to meet minimum order requirement`;
			case 60:
				return "❌ This item must be removed - no alternatives available";
			case 51:
				return "⚠️ Item discontinued and out of stock. Please remove or select a replacement.";
			case 50:
				if (classification === "Discontinued") {
					return `⚠️ Available across all locations: ${totalAvailableQty} | Requested: ${item.quantity} | Please reduce quantity to ${totalAvailableQty}`;
				}
				return `⚠️ Available: ${localAvailableQty} | Requested: ${item.quantity} | Please reduce quantity to ${localAvailableQty} or choose alternative`;
			case 41:
				return "⚠️ Item may cause delivery delays - Available at other locations.";
			case 40:
				return "⚠️ Special order item - may cause delivery delays";
			default:
				return "";
		}
	},
};

type ValidationProps = {
	cartLines: any[];
	cart: any;
	removeItem: (stockcode: string) => void;
	canEdit: boolean;
	handleRechargeableChange: (stockcode: string, newValue: boolean, quantity?: number) => void;
	updateItem: any;
	expandedReplaceItems: Set<string>;
	toggleReplaceExpansion: (stockcode: string) => void;
	noReplaceItems: Set<string>;
	handleIndividualNoReplace: (stockcode: string, checked: boolean) => void;
	noReplacementsGlobal: boolean;
	handleGlobalNoReplacements: (checked: boolean) => void;
	// Separate state for Status 40 items
	noReplacementsGlobalStatus40: boolean;
	handleGlobalNoReplacementsStatus40: (checked: boolean) => void;
};

export const CartValidation = ({
	cartLines,
	cart,
	removeItem,
	canEdit,
	handleRechargeableChange,
	updateItem,
	expandedReplaceItems,
	toggleReplaceExpansion,
	noReplaceItems,
	handleIndividualNoReplace,
	noReplacementsGlobal,
	handleGlobalNoReplacements,
	noReplacementsGlobalStatus40,
	handleGlobalNoReplacementsStatus40,
}: ValidationProps) => {
	// Use itemStatusHandler to organize items by main status groups
	const status70Items = itemStatusHandler.getItemsByMainStatus(cartLines, cart, 70);
	const status60Items = itemStatusHandler.getItemsByMainStatus(cartLines, cart, 60);
	const status50Items = itemStatusHandler.getItemsByMainStatus(cartLines, cart, 50);
	const status40Items = itemStatusHandler.getItemsByMainStatus(cartLines, cart, 40);

	// Detect mobile and set responsive values
	const isMobile = useIsMobile();
	const styles = {
		gap: isMobile ? 8 : 12,
		padding: isMobile ? 8 : 12,
		borderRadius: isMobile ? 6 : 8,
		borderWidth: isMobile ? 1 : 2,
		iconSize: isMobile ? "sm" : "md",
		iconGap: isMobile ? 6 : 8,
		marginBottom: isMobile ? 8 : 12,
		headerMarginBottom: isMobile ? 12 : 16,
		contentGap: isMobile ? 12 : 16,
	} as const;

	// console.log([...status50Items, ...status40Items, ...status60Items]);
	const resolvedErrorBright = useToken("colors", "errorbright");
	return (
		<VStack gap={styles.gap}>
			{/* Status 70 - Minimum quantity validation failed */}
			{status70Items.length > 0 && (
				<Box bg="$errorlight" padding={styles.padding} borderRadius={styles.borderRadius}>
					<Box borderWidth={styles.borderWidth} borderColor="$errorbright" bg="$basicwhite" borderRadius={styles.borderRadius} padding={styles.padding} marginBottom={styles.headerMarginBottom}>
						<HStack alignItems="center" gap={styles.iconGap}>
							<Icon as={TriangleAlertIcon} color={resolvedErrorBright} size={styles.iconSize} />
							<H4 color="$errorbright">Minimum Quantity Required</H4>
						</HStack>
						<Box marginTop={4} alignItems="flex-start">
							<ParagraphSmall color="$errorbright">The following items do not meet minimum order quantity requirements. Please increase quantities to continue.</ParagraphSmall>
						</Box>
					</Box>
					<FlatList
						data={status70Items}
						keyExtractor={(item: any, index: number) => item.item.stockcode + index.toString()}
						renderItem={({ item, index }: any) => {
							const canBeFulfilledStatus = canBeFulfilled(item, cart);

							return (
								<Box overflow="hidden" key={index} width={"$full"} bg="$basicwhite" borderWidth={styles.borderWidth} borderColor="$errormain" marginBottom={styles.marginBottom} borderRadius={styles.borderRadius}>
									<ItemListItem
										removeItem={removeItem}
										key={index}
										variant={cart.hasCompleted ? "order" : canEdit ? "checkout" : "order"}
										itemWithQuantity={item}
										showRechargeable={true}
										onRechargeableChange={handleRechargeableChange}
										fulfillmentStatus={canBeFulfilledStatus}
										hideReplaceButton={true}
									/>
									<Box padding={styles.padding} bg="">
										<ParagraphSmall color="$errormain" weight="bold">
											{itemStatusHandler.getStatusMessage(canBeFulfilledStatus, item, cart)}
										</ParagraphSmall>
									</Box>
								</Box>
							);
						}}
					/>
				</Box>
			)}

			{/* Status 60 - Items that MUST be removed */}
			{status60Items.length > 0 && (
				<Box bg="$errorlight" padding={styles.padding} borderRadius={styles.borderRadius}>
					<Box borderWidth={styles.borderWidth} borderColor="$errorbright" bg="$basicwhite" borderRadius={styles.borderRadius} padding={styles.padding} marginBottom={styles.headerMarginBottom}>
						<HStack alignItems="center" gap={styles.iconGap}>
							<Icon as={TriangleAlertIcon} color={resolvedErrorBright} size={styles.iconSize} />
							<H4 color="$errorbright">Action Required - Remove Items</H4>
						</HStack>
						<Box marginTop={4} alignItems="flex-start">
							<ParagraphSmall color="$errorbright">The following items are discontinued and cannot be fulfilled. You must remove these items from your cart to continue with checkout.</ParagraphSmall>
						</Box>
					</Box>
					<FlatList
						data={status60Items}
						keyExtractor={(item: any, index: number) => item.item.stockcode + index.toString()}
						renderItem={({ item, index }: any) => {
							const canBeFulfilledStatus = canBeFulfilled(item, cart);

							return (
								<Box overflow="hidden" key={index} width={"$full"} bg="$basicwhite" borderWidth={styles.borderWidth} borderColor="$errormain" marginBottom={styles.marginBottom} borderRadius={styles.borderRadius}>
									<ItemListItem
										removeItem={removeItem}
										key={index}
										variant={cart.hasCompleted ? "order" : canEdit ? "checkout" : "order"}
										itemWithQuantity={item}
										showRechargeable={true}
										onRechargeableChange={handleRechargeableChange}
										fulfillmentStatus={canBeFulfilledStatus}
										hideReplaceButton={true}
									/>
									<Box padding={styles.padding} bg="">
										<ParagraphSmall color="$errormain" weight="bold">
											{itemStatusHandler.getStatusMessage(canBeFulfilledStatus)}
										</ParagraphSmall>
									</Box>
								</Box>
							);
						}}
					/>
				</Box>
			)}

			{/* Status 50+ - Items that need quantity adjustment */}
			{status50Items.length > 0 && (
				<Box bg="$neutral50" overflow="hidden" padding={styles.padding} borderRadius={styles.borderRadius}>
					<Box borderWidth={styles.borderWidth} borderColor="$warningmain" overflow="hidden" bg="$warningdisabled" borderRadius={styles.borderRadius} padding={styles.padding} marginBottom={styles.headerMarginBottom}>
						<HStack alignItems="center" gap={styles.iconGap}>
							<Icon as={TriangleAlertIcon} color="black" size={styles.iconSize} />
							<H4>Discontinued Items</H4>
						</HStack>
						<Box marginTop={4} alignItems="flex-start">
							<ParagraphSmall>The following items in your order are discontinued. Please excess quantities from your cart.</ParagraphSmall>
						</Box>
					</Box>
					<FlatList
						data={status50Items}
						keyExtractor={(item: any, index: number) => item.item.stockcode + index.toString()}
						renderItem={({ item, index }: any) => {
							const canBeFulfilledStatus = canBeFulfilled(item, cart);
							const isExpanded = expandedReplaceItems.has(item.item.stockcode);

							return (
								<Box key={index} width={"$full"} bg="$basicwhite" borderWidth={styles.borderWidth} borderColor="$errormain" overflow="hidden" marginBottom={styles.marginBottom} borderRadius={styles.borderRadius}>
									<ItemListItem
										removeItem={removeItem}
										key={index}
										variant={cart.hasCompleted ? "order" : canEdit ? "checkout" : "order"}
										itemWithQuantity={item}
										showRechargeable={true}
										onRechargeableChange={handleRechargeableChange}
										fulfillmentStatus={canBeFulfilledStatus}
										onReplaceClick={() => toggleReplaceExpansion(item.item.stockcode)}
										isReplaceExpanded={isExpanded}
									/>
									<Box padding={styles.padding} gap={styles.contentGap} bg="$basicwhite">
										<Typography.ParagraphSmall color="$neutral800" weight="bold">
											{itemStatusHandler.getStatusMessage(canBeFulfilledStatus, item, cart)}
										</Typography.ParagraphSmall>
										<ItemListItemReplacementSection item={item} isExpanded={isExpanded} cart={cart} updateItem={updateItem} isNoReplace={noReplaceItems.has(item.item.stockcode)} />
									</Box>
								</Box>
							);
						}}
					/>
				</Box>
			)}

			{/* Status 40+ - Items that may cause delays */}
			{status40Items.length > 0 && (
				<Box bg="$neutral50" padding={styles.padding} borderRadius={styles.borderRadius}>
					<Box borderWidth={styles.borderWidth} borderColor="$warningmain" overflow="hidden" bg="$warningdisabled" borderRadius={styles.borderRadius} padding={styles.padding} marginBottom={styles.headerMarginBottom}>
						<HStack alignItems="center" gap={styles.iconGap}>
							<Icon as={AlertCircle} color="$infomain" size={styles.iconSize} />
							<H4 color="$infomain">Items may cause delivery delays</H4>
						</HStack>
						<Box marginTop={4} alignItems="flex-start">
							<ParagraphSmall color="$infomain">The following items may cause delivery delays. Please consider an alternative.</ParagraphSmall>
						</Box>
						<Box py={styles.padding}>
							<HStack gap={4} alignItems="center">
								<CheckBox color={"#2B6FBB"} value={noReplacementsGlobalStatus40} onValueChange={handleGlobalNoReplacementsStatus40} />
								<Typography weight="bold">I dont want any replacements.</Typography>
							</HStack>
						</Box>
					</Box>
					<FlatList
						data={status40Items}
						keyExtractor={(item: any, index: number) => item.item.stockcode + index.toString()}
						renderItem={({ item, index }: any) => {
							const canBeFulfilledStatus = canBeFulfilled(item, cart);
							const isExpanded = expandedReplaceItems.has(item.item.stockcode);
							const isNoReplace = noReplaceItems.has(item.item.stockcode);
							const borderColor = isNoReplace ? "$neutral200" : "$warningmain";

							return (
								<Box key={index} width={"$full"} bg="$basicwhite" borderWidth={styles.borderWidth} borderColor={borderColor} marginBottom={styles.marginBottom} borderRadius={styles.borderRadius} overflow="hidden">
									<ItemListItem
										removeItem={removeItem}
										key={index}
										variant={cart.hasCompleted ? "order" : canEdit ? "checkout" : "order"}
										itemWithQuantity={item}
										showRechargeable={true}
										onRechargeableChange={handleRechargeableChange}
										fulfillmentStatus={canBeFulfilledStatus}
										onReplaceClick={() => toggleReplaceExpansion(item.item.stockcode)}
										isReplaceExpanded={isExpanded}
									/>
									<Box padding={styles.padding} gap={styles.contentGap} bg="$basicwhite">
										<Typography.ParagraphSmall color="$neutral800" weight="bold">
											{itemStatusHandler.getStatusMessage(canBeFulfilledStatus)}
										</Typography.ParagraphSmall>
										<HStack gap={4} alignItems="center">
											<CheckBox color={"#2B6FBB"} value={isNoReplace} onValueChange={(checked) => handleIndividualNoReplace(item.item.stockcode, checked)} />
											<Typography weight="bold">Do not replace this item</Typography>
										</HStack>
										<ItemListItemReplacementSection item={item} isExpanded={isExpanded} cart={cart} updateItem={updateItem} isNoReplace={isNoReplace} />
									</Box>
								</Box>
							);
						}}
					/>
				</Box>
			)}
		</VStack>
	);
};

export const getValidationStatus = (cartLines: any[], cart: any, noReplaceItems?: Set<string>, currentStep?: number) => {
	// Use itemStatusHandler to organize items by main status groups
	const status70Items = itemStatusHandler.getItemsByMainStatus(cartLines, cart, 70);
	const status60Items = itemStatusHandler.getItemsByMainStatus(cartLines, cart, 60);
	const status50Items = itemStatusHandler.getItemsByMainStatus(cartLines, cart, 50);
	const status40Items = itemStatusHandler.getItemsByMainStatus(cartLines, cart, 40);

	// Find status 40+ items that haven't been resolved
	const unresolvedStatus40Items = status40Items.filter((item: any) => {
		const stockcode = item.item.stockcode;
		const isMarkedAsNoReplace = noReplaceItems ? noReplaceItems.has(stockcode) : false;
		// TODO: Add check for replaced items when replacement functionality is implemented
		const isReplaced = false; // Placeholder for future replacement logic

		return !isMarkedAsNoReplace && !isReplaced;
	});

	// Only consider Status 40+ items as blocking on Step 1
	const shouldBlockForStatus40 = currentStep === 1 && unresolvedStatus40Items.length > 0;

	return {
		hasBlockingIssues: status70Items.length > 0 || status60Items.length > 0 || status50Items.length > 0 || shouldBlockForStatus40,
		status70Count: status70Items.length,
		status60Count: status60Items.length,
		status50Count: status50Items.length,
		status40Count: status40Items.length,
		unresolvedStatus40Count: unresolvedStatus40Items.length,
	};
};

// Export aliases for backward compatibility
export const CartValidationMobile = CartValidation;
